import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign, Bell, ListChecks, Vote } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { relDay } from "@/lib/format";
import { cn } from "@/lib/utils";

const ICON = { mention: AtSign, poll: Vote, task: ListChecks };

export default function NotificationBell() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data, refetchInterval: 120000 });
  const readAll = useMutation({ mutationFn: () => api.post("/notifications/read-all"), onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });
  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    qc.invalidateQueries({ queryKey: ["notifications"] });
    if (n.link) nav(n.link);
  };
  const unread = data?.unread || 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-card text-muted-foreground transition-colors hover:text-foreground" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unread > 0 && <span data-testid="notifications-unread-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="font-display text-sm font-bold">Notifications</p>
          {unread > 0 && <button onClick={() => readAll.mutate()} className="text-xs font-semibold text-primary" data-testid="notifications-read-all">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {!data?.items?.length && <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up</p>}
          {data?.items?.map((n) => {
            const I = ICON[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`} className={cn("flex w-full gap-3 border-b px-4 py-3 text-left last:border-0 hover:bg-secondary", !n.read && "bg-primary/5")}>
                <I className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{n.title}</span>
                  {n.body && <span className="line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>}
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">{relDay(n.created_at)}</span>
                </span>
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
